import { toast } from 'sonner';
import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { hasUsableTranscript } from '../utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useEncounter } from '../api/encounters-api';
import { useGenerateDocument } from '../api/documents-api';
import { usePickerTemplates } from '@/features/templates/api/templates-api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface GenerateDocumentDialogProps {
  encounterId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  patientId: string;
}

export function GenerateDocumentDialog({ open, patientId, encounterId, onOpenChange }: GenerateDocumentDialogProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [generated, setGenerated] = useState(false);
  const [notes, setNotes] = useState('');

  const { data: templatesData } = usePickerTemplates({ active_only: true });
  const templates = templatesData ?? [];
  const selectedTmpl = templates.find((t) => t.id === selectedTemplate);

  const generateMutation = useGenerateDocument();

  const { data: encounter, isLoading: isEncounterLoading } = useEncounter(open ? encounterId : undefined);
  const hasTranscript = hasUsableTranscript(encounter?.transcript);

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a document template');
      return;
    }
    try {
      await generateMutation.mutateAsync({
        patient_id: patientId,
        encounter_ids: [encounterId],
        template_id: selectedTemplate,
        additional_context: notes ? { notes } : null,
      });
      setGenerated(true);
      toast.success(`Generating ${selectedTmpl?.name || 'document'} — it will appear shortly.`);
    } catch (err: unknown) {
      // Show what the API actually said. The generic string this used to throw
      // away is why a clinician reporting "it failed" could tell us nothing.
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not start generation. Please try again.');
    }
  };

  const handleClose = () => {
    setSelectedTemplate('');
    setNotes('');
    setGenerated(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Generate Document
          </DialogTitle>
          <DialogDescription>
            Generate an AI-powered document from this encounter's transcript.
          </DialogDescription>
        </DialogHeader>

        {generated ? (
          <div className="flex flex-col items-center justify-center py-8">
            {/*
              Generation is asynchronous — the API returns as soon as the
              workflow is scheduled. This used to claim "Document Generated" with
              a green tick and a "Status: Draft" badge at a moment when no
              document existed and the execution was still pending.
            */}
            <Loader2 className="h-12 w-12 text-muted-foreground mb-3 animate-spin" />
            <p className="font-medium text-lg">Generating…</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedTmpl?.name} is being written. It will appear in this encounter's documents when
              it is done — you can close this.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {!isEncounterLoading && !hasTranscript && (
              <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
                This encounter has no transcript yet. Record or add a transcript before generating a document.
              </div>
            )}
            {/* Template Selection */}
            <div className="space-y-2">
              <Label>Document Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((tmpl) => (
                    <SelectItem key={tmpl.id} value={tmpl.id}>
                      {tmpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes (shown for all templates) */}
            {selectedTemplate && (
              <div className="space-y-2">
                <Label>Additional Notes (Optional)</Label>
                <Textarea
                  rows={3}
                  value={notes}
                  placeholder="Any additional context for document generation..."
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            )}

            {/* Info */}
            {selectedTemplate && (
              <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
                The document will be generated using the encounter transcript as the source of truth.
                You can review and edit the document after generation.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {generated ? (
            <Button className="w-full" onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" disabled={generateMutation.isPending} onClick={handleClose}>
                Cancel
              </Button>
              <Button disabled={!hasTranscript || !selectedTemplate || generateMutation.isPending} onClick={handleGenerate}>
                {generateMutation.isPending ? (
                  <>
                    <Sparkles className="mr-2 h-4 w-4 animate-pulse" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
